import * as React from "react";
import styled from "styled-components";
import Box from "@sproutsocial/seeds-react-box";
import {
  COMMON,
  FLEXBOX,
  BORDER,
  LAYOUT,
} from "@sproutsocial/seeds-react-system-props";
import type { TypeModalFooterProps } from "../ModalTypes";
import { ModalCloseWrapper } from "./ModalCloseWrapper";

/**
 * Base styled footer component for custom modal footers.
 *
 * Use this component when you need complete control over the footer layout
 * and don't want to use the slot-based ModalFooter component.
 *
 * @example
 * <ModalCustomFooter>
 *   <YourCustomFooterContent />
 * </ModalCustomFooter>
 */
export const ModalCustomFooter = styled(Box)`
  flex: 0 0 auto;
  font-family: ${(props) => props.theme.fontFamily};
  background-color: ${(props) => props.theme.colors.container.background.base};
  padding: ${(props) => props.theme.space[400]};
  border-bottom-right-radius: ${(props) => props.theme.radii[800]};
  border-bottom-left-radius: ${(props) => props.theme.radii[800]};
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${(props) => props.theme.space[100]};

  ${COMMON}
  ${FLEXBOX}
  ${BORDER}
  ${LAYOUT}
`;

ModalCustomFooter.displayName = "ModalCustomFooter";

/**
 * Modal footer component for action buttons.
 *
 * This component only supports slot-based rendering with button props.
 * For custom footer layouts, use ModalCustomFooter instead.
 *
 * Provides automatic button wrapping and layout management. Supports three button positions:
 * - primaryButton: Main action button on the right (auto-closes modal)
 * - cancelButton: Secondary action on the right (auto-closes modal)
 * - leftAction: Optional action on the left (no auto-close, for destructive actions)
 *
 * @example
 * <ModalFooter
 *   cancelButton={<Button>Cancel</Button>}
 *   primaryButton={<Button appearance="primary">Save</Button>}
 * />
 *
 * @example
 * // With left action
 * <ModalFooter
 *   leftAction={<Button appearance="destructive">Delete</Button>}
 *   cancelButton={<Button>Cancel</Button>}
 *   primaryButton={<Button appearance="primary">Save</Button>}
 * />
 */
export const ModalFooter = (props: TypeModalFooterProps) => {
  const {
    cancelButton,
    primaryButton,
    leftAction,
    closeOnPrimaryAction = true,
    ...rest
  } = props;

  // If no simplified props provided, return empty footer
  if (!cancelButton && !primaryButton && !leftAction) {
    return null;
  }

  const wrappedPrimaryButton =
    primaryButton && closeOnPrimaryAction ? (
      <ModalCloseWrapper>{primaryButton}</ModalCloseWrapper>
    ) : (
      primaryButton
    );

  // Build simplified API layout
  return (
    <ModalCustomFooter data-slot="modal-footer" data-qa-modal-footer {...rest}>
      {/* Left action (e.g., Delete button) */}
      {leftAction ? leftAction : null}

      {/* Right-aligned button group (Cancel + Primary) */}
      <Box display="flex" gap={300} marginLeft="auto">
        {cancelButton && <ModalCloseWrapper>{cancelButton}</ModalCloseWrapper>}
        {wrappedPrimaryButton}
      </Box>
    </ModalCustomFooter>
  );
};

ModalFooter.displayName = "ModalFooter";
